// Reprise manuelle du dernier recours serveur (bandeau « Problème signalé »,
// A7, C21) : ré-invoque `assemble-capture` pour une capture `assembly_failed`.
// Même patron d'étanchéité que `triggerTranscription`/`retryExtraction`
// (lib/captures/transcribe.ts, DS §6.13) : le motif technique part au
// journal, l'écran ne reçoit qu'une phrase à nous — jamais un des mots
// techniques interdits à l'écran (take-copy.ts).
//
// L'edge répond TOUJOURS 200 avec `{ result }` (`_shared/assemble-capture.ts`) :
// `result.status` vaut "captured" (réparée), "assembly_failed" (échoue encore)
// ou "skipped" (marqueur absent/ligne disparue) — JAMAIS un statut HTTP
// d'erreur pour un résultat métier. `error` (branche `ok:false` ci-dessous) ne
// couvre donc que l'ÉCHEC DE L'APPEL lui-même (réseau, 4xx/5xx) : dans ce cas
// seulement, rien n'a changé côté serveur — l'écran doit le dire sans reprendre
// la formulation de `TAKE_COPY.problemBody` (« l'équipe est prévenue, tu n'as
// rien à faire »), qui ne s'applique qu'à un ÉCHEC MÉTIER déjà tracé.
import type { SupabaseClient } from "@supabase/supabase-js";
import { invokeErrorMessage } from "../supabase/invoke-error";
import { segmentsMissingOf } from "./segment-upload";

// Micro-texte de repli pour un échec de L'APPEL. Volontairement minimal, sans
// promesse ni geste inventé : contrairement à `TAKE_COPY.problemBody`, on ne
// sait PAS ici que l'équipe est prévenue (le signalement automatique porte
// sur l'échec D'ASSEMBLAGE, pas sur un raté de CE clic-ci).
export const ASSEMBLAGE_RELANCE_ECHEC =
  "Ça n'a pas marché. Ton enregistrement est toujours en sécurité. Réessaie plus tard.";

export type RetryAssemblyResult =
  | {
      ok: true;
      status: string;
      /** Segments absents du serveur (S-m3) : la prise doit les renvoyer. */
      missing?: string[];
    }
  | { ok: false; error: string };

interface AssembleCaptureResponse {
  result?: { status?: unknown };
}

export async function retryAssembly(
  client: SupabaseClient,
  captureId: string,
): Promise<RetryAssemblyResult> {
  const { data, error } = await client.functions.invoke("assemble-capture", {
    body: { capture_id: captureId },
  });
  if (error) {
    return {
      ok: false,
      error: await invokeErrorMessage("assemble-capture", error, ASSEMBLAGE_RELANCE_ECHEC),
    };
  }
  const status = (data as AssembleCaptureResponse | null)?.result?.status;
  const missing = segmentsMissingOf(data);
  return {
    ok: true,
    status: typeof status === "string" ? status : "",
    ...(missing && missing.length > 0 ? { missing } : {}),
  };
}
